/* ============================================================
   melusio-product.js — Melusio PDP behaviour (Cut 4).

   <melusio-gallery>: desktop = one active slide switched by a thumbnail rail (role=tablist,
   roving tabindex, ArrowLeft/Right); mobile = the slide track is a scroll-snap carousel and
   dots reflect/scrub it. No zoom/lightbox in v1.

   The buy box (size selector + add-to-bag) is the SHARED engine (melusio-buy-box.js) with
   urlSync on (replaceState ?variant=) and onSelect syncing the Buy-it-now form's variant id.
   ============================================================ */
(function () {
  class MelusioGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-track]');
      this.slides = Array.from(this.querySelectorAll('[data-pdp-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
      this.dots = Array.from(this.querySelectorAll('[data-pdp-dot]'));
      if (this.slides.length < 2) return;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.activate(+t.dataset.index, true)));
      this.dots.forEach((d) => d.addEventListener('click', () => this.scrollToSlide(+d.dataset.index)));

      // roving tabindex + arrow keys on the thumb rail
      const rail = this.querySelector('[data-pdp-thumbs]');
      if (rail) rail.addEventListener('keydown', (e) => {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        const cur = this.thumbs.findIndex((t) => t.tabIndex === 0);
        const next = (cur + (e.key === 'ArrowRight' ? 1 : -1) + this.thumbs.length) % this.thumbs.length;
        this.activate(next, true);
        this.thumbs[next].focus();
      });

      // mobile: reflect scroll position in the dots
      if (this.track) this.track.addEventListener('scroll', () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setActiveMarkers(i);
        });
      }, { passive: true });
    }

    activate(index, updateDesktopSlide) {
      // desktop: swap the visible slide; mobile CSS shows the track regardless
      if (updateDesktopSlide) this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.setActiveMarkers(index);
    }

    setActiveMarkers(index) {
      this.thumbs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
    }

    scrollToSlide(index) {
      if (this.track) this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'smooth' });
    }
  }
  if (!customElements.get('melusio-gallery')) customElements.define('melusio-gallery', MelusioGallery);

  // ---- buy box ----
  function initBuyBox() {
    const box = document.querySelector('[data-buy-box]');
    if (!box) return;
    const buyNowId = document.querySelector('[data-buy-now-id]');
    const wireBox = () => {
      if (!window.MelusioBuyBox) return; // engine unavailable — leave the buy box server-rendered
      window.MelusioBuyBox.wire(box, {
        urlSync: box.hasAttribute('data-url-sync'),
        // A null variant means a group lost its pick, which only happens on a product the
        // server could not resolve at all — and that product renders no Buy-it-now form.
        // Leaving the last good id in place beats writing an empty one into a live form.
        onSelect: (variant) => { if (buyNowId && variant) buyNowId.value = variant.id; },
      });
    };
    // The engine (melusio-buy-box.js) loads GLOBALLY from theme.liquid's HEAD — i.e. BEFORE this
    // section's deferred script in document order — so window.MelusioBuyBox normally exists and we
    // wire synchronously (this is what un-gates the hydration-disabled CTA earliest). KEEP the
    // DOMContentLoaded fallback: if the engine ever moves back after us (or a script between us
    // fails to parse the order guarantee away), all deferred scripts still run before DCL, so the
    // engine is ready by then. Without it the PDP pills get no click listener (regression 6e85b1d).
    if (window.MelusioBuyBox) wireBox();
    else document.addEventListener('DOMContentLoaded', wireBox, { once: true });
  }

  // ---- sticky buy bar (phones; CSS hides it from 750px up) ----
  //
  // A PROJECTION of the inline CTA, not a second buy box. Everything the bar shows is
  // read back off [data-qv-add] and the [data-qv-error] beside it — what the shared
  // engine writes — and every click it takes is forwarded to that same button. So the
  // failure message has one author and one wording, and the PR #65 combination resolver,
  // the add-response restore path and all three hydration guards keep their single site,
  // and the tracksuit's two option groups need no code here at all: by the time the
  // engine has resolved the intersection it has already written the answer where the bar
  // reads it.
  //
  // Guard the bar inherits: its markup renders `disabled` for the same reason the inline
  // CTA does, and only sync() below can lift that — never a marker of its own.
  function initStickyBuy() {
    const box = document.querySelector('[data-buy-box]');
    const bar = document.querySelector('[data-buy-bar]');
    if (!box || !bar) return;
    const cta = box.querySelector('[data-qv-add]');
    const barCta = bar.querySelector('[data-buy-bar-add]');
    if (!cta || !barCta) return;
    const ctaLabel = cta.querySelector('[data-qv-cta-label]');
    const barPrice = bar.querySelector('.ds-price__amount');
    // The engine's failure region, and the bar's copy of it. Read for the same reason
    // the label is read off the CTA: one writer, so the copy can never say something
    // the buy box did not say, and no locale string lands on this side.
    const err = box.querySelector('[data-qv-error]');
    const barErr = bar.querySelector('[data-buy-bar-error]');

    // The SAME server-rendered map the engine resolves against. Looking a price up by the
    // id the engine has already chosen is not a second resolver — it is the lookup the
    // engine itself does — and price_money arrives pre-formatted from Liquid, so the rule
    // that money is never formatted in JS still holds here.
    let variants = [];
    const mapEl = box.querySelector('[data-qv-variants]');
    if (mapEl) {
      try { variants = JSON.parse(mapEl.textContent) || []; } catch (e) { variants = []; }
    }

    // PICKED is whether the shopper chose a size, as opposed to the server preselecting
    // one. The server renders the product with a size already selected (the store's
    // default, M), and the bar used to add it: on a phone the sizes sit under the bar, so
    // a shopper could add M without ever seeing a size (#222). Until they pick, the bar
    // asks for a size and takes them to the sizes instead. Arriving on a link to one size
    // (?variant=) counts as a pick.
    let picked = new URLSearchParams(window.location.search).has('variant');
    let choosing = true;
    const sizes = box.querySelector('[data-qv-options], [data-qv-sizes]');
    const addSizeLabel = bar.getAttribute('data-add-size-label') || '';

    function sync() {
      const full = ctaLabel ? ctaLabel.textContent : '';
      const selectLabel = cta.getAttribute('data-select-label') || full;
      const id = cta.getAttribute('data-variant-id');
      const variant = id && variants.find((v) => String(v.id) === id);
      // Three states. CHOOSING: no size picked yet, or a group has lost its pick; the
      // bar is live and scrolls to the sizes. BUSY or SOLD OUT: the engine disabled the
      // button with its own bare label ("Adding…", "Sold out"), echoed whole. READY: the
      // bar names the size it will add.
      choosing = !picked || (cta.disabled && full === selectLabel);
      let label;
      if (choosing) {
        barCta.disabled = false;
        label = selectLabel;
        barCta.setAttribute('aria-label', label);
      } else if (cta.disabled || !variant) {
        barCta.disabled = true;
        label = full;
        barCta.setAttribute('aria-label', label);
      } else {
        barCta.disabled = false;
        // One size when both garments share it (the tracksuit's joint row), both when
        // they differ.
        const size = [...new Set(variant.options)].join(' / ');
        label = addSizeLabel ? addSizeLabel.replace('[size]', size) : (cta.getAttribute('data-add-label') || full);
        // Priced accessible name over a bare visible one: it still starts with the
        // visible label, so it matches what a speech user would say to invoke it.
        barCta.setAttribute('aria-label', label + ' · ' + variant.price_money);
      }
      barCta.textContent = label;
      if (variant && barPrice) barPrice.textContent = variant.price_money;
      // Text AND hidden, so the copy clears whenever the source clears: on a new pick,
      // and on the retry that follows a refusal. The source keeps the only role=alert;
      // this one is aria-hidden in the markup, so the failure is seen twice and
      // announced once.
      if (err && barErr) {
        barErr.textContent = err.textContent;
        barErr.hidden = err.hidden;
      }
    }

    barCta.addEventListener('click', () => {
      if (!choosing) { if (!cta.disabled) cta.click(); return; }
      if (!sizes) return;
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      sizes.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      const pill = sizes.querySelector('.is-selected:not([disabled])') || sizes.querySelector('button:not([disabled])');
      if (pill) pill.focus({ preventScroll: true });
    });

    // A pill press is a pick, even on the size the server had already selected, which
    // changes nothing on the button and so would not reach the observers below.
    box.addEventListener('click', (e) => {
      if (!e.target.closest('[data-qv-size], [data-qv-joint-size]')) return;
      picked = true;
      sync();
    });

    // Watches the CTA itself and the label inside it, which between them carry every
    // state change the engine makes: selection, Adding…, the restore after a response,
    // and a group losing its pick.
    new MutationObserver(sync).observe(cta, {
      attributes: true, attributeFilter: ['disabled', 'data-variant-id'],
      childList: true, characterData: true, subtree: true,
    });

    // A second observer, on the failure region. The message is written into a SIBLING
    // of the CTA, and nothing on the CTA itself changes when it appears, so the
    // observer above cannot see it.
    if (err && barErr) new MutationObserver(sync).observe(err, {
      attributes: true, attributeFilter: ['hidden'],
      childList: true, characterData: true, subtree: true,
    });

    sync();

    // Show it once the inline button has scrolled out of view, hide it when that button
    // comes back — an IntersectionObserver, so the browser reports the crossing itself
    // and nothing of ours runs per scroll frame.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 }).observe(cta);
    }
  }

  // ---- product-info accordion ----
  // Each section toggles independently (NOT one-at-a-time; not wired to the overlay system).
  // Native <button> headers already give Enter/Space; we add roving Arrow/Home/End across headers.
  function initAccordion() {
    const acc = document.querySelector('[data-accordion]');
    if (!acc) return;
    const triggers = Array.from(acc.querySelectorAll('[data-acc-trigger]'));
    triggers.forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        const panel = document.getElementById(btn.getAttribute('aria-controls'));
        if (panel) panel.hidden = expanded;
      });
      btn.addEventListener('keydown', (e) => {
        const keys = { ArrowDown: 1, ArrowUp: -1, Home: 'first', End: 'last' };
        if (!(e.key in keys)) return;
        e.preventDefault();
        const i = triggers.indexOf(btn);
        let next;
        if (keys[e.key] === 'first') next = 0;
        else if (keys[e.key] === 'last') next = triggers.length - 1;
        else next = (i + keys[e.key] + triggers.length) % triggers.length;
        triggers[next].focus();
      });
    });
  }

  // initBuyBox first: it wires the engine synchronously when the engine is present, so
  // the bar's first sync() reads a CTA whose hydration gate has already been consumed.
  function initPDP() { initBuyBox(); initStickyBuy(); initAccordion(); }
  if (document.readyState !== 'loading') initPDP();
  else document.addEventListener('DOMContentLoaded', initPDP);
})();
